import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Observable, Subscription, filter, map, of, take } from 'rxjs';

import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { coverageInsightTypesOptions } from '@Common/Forms/data/coverage-insight-types-options';
import { RichTextEditorConfigModel } from '@Common/Forms/components/RichTextEditorCK/model/rich-text-editor-config.model';
import { BlockModel } from '@Common/PageBuilder/models/block.model';
import { AiAnalysisTypes } from '../../../../Common/Mentions/enums/ai-analysis-types.enum';
import { RichTextEditorCkComponent } from '@Common/Forms/components/RichTextEditorCK/rich-text-editor-ck.component';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';

import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';

import { MentionSearchService } from '@Monitor/Core/services/mention-search.service';
import { MonitoringBackEndDataSourceModel } from '@Monitor/Shared/models/monitoring-back-end-data-source.model';
import { MonitoringSourcePickerFieldModel } from '@Monitor/Shared/models/monitoring-source-picker-field.model';

import { defaultAnalysisSourcePickerField } from '../../models/briefings-block-types.model';
import { AlertTypeEnum } from '@Common/MonitorShared/enum/alert-type.enum';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { BriefingBlockTypeEnum } from '../../enums/briefing-block-type.enum';
import { AlertSurgeDisplayElementModel } from '@Monitor/Alerts/models/alert-surge-display-element.model';
import { CoverageInsights } from '../../../Shared/models/coverage-insights.model';
import { AnalysisDisplayElementModel } from '../../models/briefing-analysis-display-element.model';
import { CustomInsights } from '../../../Shared/models/custom-insights.model';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { BriefingCoverageAnalysisService } from '../../services/briefing-coverage-analysis.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { CustomInsightType } from '@Monitor/Shared/models/custom-insights-type.model';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import {
    CustomPromptsManagementDialogComponent
} from '../../../../Admin/AccountManagement/components/CustomPromptsManagement/custom-prompts-management-dialog.component';

@Component({
    selector: 'agpr-generate-analysis-dialog',
    templateUrl: './generate-analysis-dialog.component.html',
    styleUrls: ['./generate-analysis-dialog.component.scss'],
    providers: [
        // we need to have it's own MentionSearchService,
        // because the RHP component will override it in some scenarios
        MentionSearchService
    ],
    standalone: false
})
export class GenerateAnalysisDialogComponent extends AbstractDialogComponent implements OnDestroy, OnInit {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly coverageInsightTypes = AiAnalysisTypes;
    readonly MAX_NUM_DYNAMIC_MENTIONS_ANALYZED: number = 10000;
    readonly MAX_NAME_LENGTH: number = 64;
    readonly MAX_PROMPT_LENGTH: number = 20000;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() public set source(source: MonitoringBackEndDataSourceModel) {
        this.dataSource = source;
    }

    id: string = 'generate_analysis_dialog';
    promptIsValid: boolean = true;
    hideSlideOverlay: boolean = false;
    promptGenerateTooltip: string;
    generatedContent: string = '';
    selectedTabIndex: number = 0;
    isInsightDirty: boolean = false;
    openAiPermission: boolean = false;

    coverageInsightTypesOptions = coverageInsightTypesOptions;
    selectedCoverageInsightType: AiAnalysisTypes;

    promptLanguageOptions: DropdownOptionModel[] = [];
    selectedPromptLanguageOption: DropdownOptionModel;

    sourceField: MonitoringSourcePickerFieldModel = defaultAnalysisSourcePickerField;
    dataSource: MonitoringBackEndDataSourceModel;

    private surgeSensitivity?: number;

    selectedLengthOption: DropdownOptionModel;
    lengthOptions: DropdownOptionModel[] = [];

    coverageInsights: CoverageInsights;
    customInsights: CustomInsights[];
    customInsight: CustomInsights;
    customInsightCopy: CustomInsights;
    customInsightValid: boolean = true;
    customInsightSaveTooltip: string;

    selectedBlock: BlockModel;

    systemInsights: AiAnalysisTypes[] = [
        AiAnalysisTypes.EXECUTIVE_SUMMARY,
        AiAnalysisTypes.KEY_TAKEAWAYS,
        AiAnalysisTypes.BRAND_IMPACT,
        AiAnalysisTypes.SENTIMENT_OPINION,
        AiAnalysisTypes.TOP_NARRATIVES,
        // AiAnalysisTypes.CUSTOM
    ];

    ckEditorConfig = new RichTextEditorConfigModel({
        enableInsertImage: true,
        enableLineHeight: true,
        enableFontColor: true,
        enableBackgroundColor: true,
        enableHtmlEditor: true,
        enableFontFamily: true,
        enableFontSize: true,
        enableBlockQuote: true,
        enableHeading: true,
        enableAlignment: true,
        enableDirection: true,
        enableIndent: true,
        enableTranslation: false,
        enableAIHelper: false,
        enableAIRefinement: true,
        hideInsert: true,
        aiReferenceId: ''
    });

    @ViewChild('insightEditor') insightEditor: RichTextEditorCkComponent;

    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private briefingsService: AbstractPageBuilderService,
        public localizationService: LocalizationService,
        private briefingCoverageAnalysisService: BriefingCoverageAnalysisService,
        public mentionSearchService: MentionSearchService,
        private currentUserStateService: CurrentUserStateService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        super(coreDialogService, 'GenerateAnalysisDialog');
        this.briefingCoverageAnalysisService.clearGeneratedContent();
    }

    ngOnInit(): void {
        this.briefingsService.getFocusedResource().pipe(
            filter(x => !!x),
            take(1)
        ).subscribe(resource => {
            if ((resource as any).alertType === AlertTypeEnum.SURGE) {
                this.sourceField = {
                    ...this.sourceField,
                    dataSourceOptions: [
                        new DropdownOptionModel('Monitoring.sourcePickerField.surgeSource', MonitoringSeriesTypeEnum.coverageSurge),
                        ...this.sourceField.dataSourceOptions
                    ]
                } as MonitoringSourcePickerFieldModel;
                this.systemInsights = [AiAnalysisTypes.COVERAGE_SURGE_INSIGHT, ...this.systemInsights];

                const surgeBlock = ((resource as any).blocks as BlockModel[])?.find(
                    block => block.type?.key === BriefingBlockTypeEnum.SURGE_ALERT
                );
                this.surgeSensitivity = (surgeBlock?.displayElement as AlertSurgeDisplayElementModel)?.sensitivity;
            }
        });

        this.subscriptions.push(
            this.briefingsService.getSelectedBlock().pipe(filter(x => !!x)).subscribe(
                (block) => {
                    this.selectedBlock = block;
                    if ((this.selectedBlock.displayElement as AnalysisDisplayElementModel).text) {
                        this.generatedContent = (this.selectedBlock.displayElement as AnalysisDisplayElementModel).text;
                    }

                    if ((this.selectedBlock.displayElement as AnalysisDisplayElementModel).analysisField) {
                        this.coverageInsights = (this.selectedBlock.displayElement as AnalysisDisplayElementModel).analysisField.clone();

                        if (this.coverageInsights.aiAnalysisType) {
                            if (this.coverageInsights.aiAnalysisType === AiAnalysisTypes.CUSTOM) {
                                if (this.coverageInsights.customPromptId) {
                                    this.selectedCoverageInsightType = this.coverageInsights.aiAnalysisType;
                                }
                                this.selectedTabIndex = 1;
                                this.selectInitialCustomInsight();
                            } else {
                                this.selectedCoverageInsightType = this.coverageInsights.aiAnalysisType;
                            }
                        }
                    } else {
                        this.coverageInsights = new CoverageInsights();
                    }
                }
            ),
            this.briefingCoverageAnalysisService.getGeneratedContent().pipe(filter(x => !!x)).subscribe(
                (generatedContent) => {
                    this.generatedContent = generatedContent;
                    this.changeDetectorRef.detectChanges();
                }
            ),
            this.briefingCoverageAnalysisService.customInsights$.pipe(
                map(x => x.filter(item => item.promptType === CustomInsightType.BRIEFING_ANALYSIS_BLOCK))
            ).subscribe(customInsights => {
                this.customInsights = customInsights;
                this.selectInitialCustomInsight();
            }),
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                this.promptLanguageOptions.forEach(option => {
                    option.label = tag + '.' + option.value;
                });
            }),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
        );
    }

    sourceSelected(dataSource: MonitoringBackEndDataSourceModel): void {
        this.dataSource = dataSource;
        (this.selectedBlock.displayElement as AnalysisDisplayElementModel).backendDataSource = this.dataSource;
        this.validatePromptFields();
    }

    selectInitialCustomInsight() {
        if (!this.customInsight &&
            ((this.selectedCoverageInsightType as AiAnalysisTypes) === AiAnalysisTypes.CUSTOM) &&
            this.coverageInsights.customPromptId &&
            this.customInsights?.length
        ) {
            const initialCustomInsight = this.customInsights.find(customInsight => this.coverageInsights.customPromptId === customInsight.id);
            if (initialCustomInsight) {
                this.selectCustomInsight(initialCustomInsight);
            }
        }
    }

    tabIndexChange(index: number): void {
        this.selectedCoverageInsightType = null;
        this.selectedTabIndex = index;

        if (index) {
            this.coverageInsights.aiAnalysisType = AiAnalysisTypes.CUSTOM;
        } else {
            this.customInsight = null;
            this.coverageInsights.customPromptId = null;
        }

        this.validatePromptFields();
    }

    selectAnalysisType(type: AiAnalysisTypes): void {
        this.selectedCoverageInsightType = type;
        this.coverageInsights.aiAnalysisType = type;
        this.coverageInsights.selectedLengthOption = null;

        this.coverageInsights = this.briefingCoverageAnalysisService.setDefaults(this.coverageInsights);

        if (this.coverageInsights.aiAnalysisType === AiAnalysisTypes.EXECUTIVE_SUMMARY) {
            this.coverageInsights.keyTakeawayHeading = 'Key Takeaways';
        }

        this.validatePromptFields();
    }

    selectCustomInsight(insight: CustomInsights): void {
        this.customInsight = insight.clone();
        this.customInsightCopy = insight.clone();
        this.selectedCoverageInsightType = AiAnalysisTypes.CUSTOM;
        this.validatePromptFields();
    }

    newCustomInsight() {
        if (!this.selectedTabIndex) {
            this.tabIndexChange(1);
        }
        setTimeout(() => {
            this.coverageInsights.aiAnalysisType = AiAnalysisTypes.CUSTOM;
            this.customInsight = new CustomInsights();
            this.customInsight.promptType = CustomInsightType.BRIEFING_ANALYSIS_BLOCK;
            this.selectedCoverageInsightType = AiAnalysisTypes.CUSTOM;
            this.customInsightCopy = null;
            this.validatePromptFields();
        }, 0);
    }

    backToPrompts(): void {
        this.selectedCoverageInsightType = null;
        this.customInsight = null;
        this.insightEditor?.clearRefineParagraphButton();
    }

    changedValue(value: CoverageInsights) {
        this.coverageInsights = value;
        this.validatePromptFields();
    }

    changedCustomValue(value: CustomInsights) {
        this.customInsight = value;
        this.validatePromptFields();
    }

    saveCustomInsight() {
        if (this.customInsight?.id) {
            this.briefingCoverageAnalysisService.updateCustomInsight(this.customInsight).subscribe(success => {
                if (success) {
                    this.customInsightCopy = this.customInsight.clone();
                }
            });
        } else {
            this.doCreateCustomInsight();
        }
    }

    doCreateCustomInsight() {
        this.briefingCoverageAnalysisService.createCustomInsight(this.customInsight.clone()).subscribe(customInsightId => {
            if (customInsightId) {
                this.customInsight.id = customInsightId;
                this.customInsightCopy = this.customInsight.clone();
            }
        });
    }

    async saveAsCustomInsight() {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'nameCustomInsight',
                heading: 'Briefings.analysis.customInsight.header',
                okLabel: 'Briefings.analysis.customAnalysis.save',
                placeholder: 'Briefings.analysis.customAnalysisName.placeholder',
                description: 'Briefings.analysis.customInsight.label',
                initialValue: this.customInsight.name,
                submit: (name: string) => {
                    this.customInsight.name = name;
                    this.doCreateCustomInsight();
                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    async generateInsight() {
        if (this.selectedCoverageInsightType === AiAnalysisTypes.CUSTOM) {
            this.coverageInsights.customPromptId = this.customInsight.id;
        }
        // increased flag is for coverage surge insights: true when the alert sensitivity is over 100%
        if (this.coverageInsights.aiAnalysisType === AiAnalysisTypes.COVERAGE_SURGE_INSIGHT) {
            this.coverageInsights.increased = (this.surgeSensitivity ?? 0) > 100;
            this.coverageInsights.includeStrategy = this.coverageInsights.includeStrategy ?? false;
            this.coverageInsights.includeSentiment = this.coverageInsights.includeSentiment ?? false;
            this.coverageInsights.includeThemes = this.coverageInsights.includeThemes ?? false;
        }

        this.briefingCoverageAnalysisService.generateAnalysis(this.coverageInsights, this.dataSource);
    }

    deleteCustomInsight(event, insight: CustomInsights) {
        event.stopPropagation();

        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Ai.analysis.customInsight.delete',
                message: this.localizationService.transform('Ai.analysis.customInsight.delete.confirm', { name: insight.name }),
                alertType: messageSeverityEnum.warning,
                okLabel: 'Core.button.delete',
                id: 'BriefingsDelete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.briefingCoverageAnalysisService.deleteCustomInsight(insight);
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    validatePromptFields(): void {
        if (!this.coverageInsights.aiAnalysisType) {
            this.promptIsValid = false;
            this.promptGenerateTooltip = 'Briefings.analysis.errors.mustSelectAnalysisType';
            return;
        }

        switch (this.selectedCoverageInsightType) {
            case AiAnalysisTypes.EXECUTIVE_SUMMARY:
                if (this.coverageInsights) {
                    if (!this.coverageInsights.selectedLanguageOption) {
                        this.promptIsValid = false;
                        this.promptGenerateTooltip = 'Briefings.analysis.errors.mustSelectLanguage';
                        return;
                    }
                    if (!this.coverageInsights.selectedLengthOption) {
                        this.promptIsValid = false;
                        this.promptGenerateTooltip = 'Briefings.analysis.errors.mustSelectLength';
                        return;
                    }
                }

                this.promptIsValid = true;
                this.promptGenerateTooltip = '';

                break;
            case AiAnalysisTypes.SENTIMENT_OPINION:
            case AiAnalysisTypes.KEY_TAKEAWAYS:
                if (this.coverageInsights) {
                    if (!this.coverageInsights.selectedLanguageOption) {
                        this.promptIsValid = false;
                        this.promptGenerateTooltip = 'Briefings.analysis.errors.mustSelectLanguage';
                        return;
                    }
                }

                this.promptIsValid = true;
                this.promptGenerateTooltip = '';

                break;
            case AiAnalysisTypes.BRAND_IMPACT:
                if (this.coverageInsights) {
                    if (!this.coverageInsights.selectedLanguageOption) {
                        this.promptIsValid = false;
                        this.promptGenerateTooltip = 'Briefings.analysis.errors.mustSelectLanguage';
                        return;
                    }
                    if (!this.coverageInsights.brand) {
                        this.promptIsValid = false;
                        this.promptGenerateTooltip = 'Briefings.analysis.errors.mustAddBrand';
                        return;
                    }
                }

                this.promptIsValid = true;
                this.promptGenerateTooltip = '';

                break;
            case AiAnalysisTypes.CUSTOM:
                if (!this.customInsight?.name?.trim()) {
                    this.promptIsValid = false;
                    this.customInsightValid = false;
                    this.promptGenerateTooltip = 'Briefings.analysis.errors.nameRequired';
                    this.customInsightSaveTooltip = 'Briefings.analysis.errors.nameRequired';
                    return;
                }

                if (this.customInsight?.name?.length >= this.MAX_NAME_LENGTH) {
                    this.promptIsValid = false;
                    this.customInsightValid = false;
                    this.customInsightSaveTooltip = 'Briefings.analysis.errors.nameMaxLength';
                    this.promptGenerateTooltip = 'Briefings.analysis.errors.nameMaxLength';
                    return;
                }

                const isNameDuplicate = this.customInsights?.some(item => {
                    return item.name.toLowerCase() === this.customInsight?.name?.trim().toLowerCase() && item.id !== this.customInsight?.id;
                });
                if (isNameDuplicate) {
                    this.promptIsValid = false;
                    this.customInsightValid = false;
                    this.promptGenerateTooltip = 'Briefings.analysis.errors.uniqueNameRequired';
                    this.customInsightSaveTooltip = 'Briefings.analysis.errors.uniqueNameRequired';
                    return;
                }

                if (!this.customInsight?.prompt?.trim()) {
                    this.promptIsValid = false;
                    this.customInsightValid = false;
                    this.promptGenerateTooltip = 'Briefings.analysis.errors.promptRequired';
                    this.customInsightSaveTooltip = 'Briefings.analysis.errors.promptRequired';
                    return;
                }

                if (this.customInsight?.prompt?.length >= this.MAX_PROMPT_LENGTH) {
                    this.promptIsValid = false;
                    this.customInsightValid = false;
                    this.customInsightSaveTooltip = 'Briefings.analysis.errors.promptMaxLength';
                    this.promptGenerateTooltip = 'Briefings.analysis.errors.promptMaxLength';
                    return;
                }

                if (!this.customInsight?.isValid()) {
                    this.customInsightValid = false;
                    this.promptIsValid = false;
                    return;
                }
                if (!this.customInsight.equals(this.customInsightCopy)) {
                    this.promptIsValid = true; // prompt is valid, we want to show the save buttons, but we want to show the tooltip on the generate button
                    this.customInsightValid = true;
                    this.promptGenerateTooltip = 'Briefings.analysis.errors.mustSave';
                    this.customInsightSaveTooltip = '';
                    return;
                }

                this.promptIsValid = true;
                this.promptGenerateTooltip = '';
                this.customInsightValid = true;
                this.customInsightSaveTooltip = '';
                break;
            default:
                this.promptIsValid = true;
                this.promptGenerateTooltip = '';
                break;
        }

        if ((!this.dataSource.curatedMentionCount) && (!this.dataSource.dynamicMentionCount)) {
            this.promptIsValid = false;
            this.promptGenerateTooltip = 'Briefings.analysis.errors.noMentionsToAnalyze';
        }

        if (this.dataSource.dynamicMentionCount > this.MAX_NUM_DYNAMIC_MENTIONS_ANALYZED) {
            this.promptIsValid = false;
            this.promptGenerateTooltip = this.localizationService.transform(
                'Briefings.analysis.errors.tooManyDynamicMentions', { count: this.dataSource.dynamicMentionCount }
            );
        }
    }

    close(): void {
        // HACK to get the text to show up in rich text editor block, because it only sets it on ngInit
        // also needed to redraw RHP after
        this.briefingsService.selectBlock(this.selectedBlock);
        this.ngOnDestroy();
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    apply() {
        (this.selectedBlock.displayElement as AnalysisDisplayElementModel).text = this.generatedContent;
        (this.selectedBlock.displayElement as AnalysisDisplayElementModel).analysisField = this.coverageInsights.clone();

        this.close();
    }

    openCustoPromptDialog() {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: document.getElementById('main-content'),
                selectedPromptType: CustomInsightType.BRIEFING_ANALYSIS_BLOCK
            },
            outputs: {
                dialogClose: () => {
                    const selectCustomInsightId = this.customInsight?.id;
                    if (selectCustomInsightId) {
                        const updatedInsight = this.customInsights.find((item) => item.id === selectCustomInsightId);
                        if (updatedInsight) {
                            this.selectCustomInsight(updatedInsight);
                        } else {
                            this.backToPrompts();
                        }
                    }
                }
            }
        };
        this.coreDialogService.open(CustomPromptsManagementDialogComponent, io);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
